import { ClassifierData } from 'types/classifiers';
import {
    CorrespondenceCreateDto,
    CorrespondenceDetailsDto,
    CorrespondenceDirectionType,
    CorrespondenceForm,
    CorrespondencePartyRoleType,
    CorrespondenceStatusType,
    CorrespondenceType,
} from 'types/correspondence';
import { formatAddressSimple } from 'utils/addressFormatter';
import { getClassifierOptionByCode } from 'utils/formUtil';
import { sortByField } from 'utils/sort';

/** The classifiers {@link correspondenceDtoToForm} and the form's fields read. */
export const CORRESPONDENCE_FORM_CLASSIFIERS = [
    'CORRESPONDENCE_DIRECTION',
    'CORRESPONDENCE_TYPE',
    'CORRESPONDENCE_STATUS_TYPE',
    'CORRESPONDENCE_CATEGORY_TYPE',
];

/**
 * The correspondence form of a stored correspondence. A correspondence with no sender or recipient maps to an empty
 * list, which the form shows as one empty entry.
 */
export const correspondenceDtoToForm = (
    correspondence: CorrespondenceDetailsDto,
    classifiers: Record<string, ClassifierData>
): CorrespondenceForm => ({
    readOnlyNumber: correspondence.number,
    name: correspondence.name ?? '',
    typeCode: getClassifierOptionByCode<string, string>(classifiers['CORRESPONDENCE_TYPE'], correspondence.typeCode),
    directionTypeCode: CorrespondenceDirectionType[correspondence.directionTypeCode],
    categoryTypeCode: getClassifierOptionByCode<string, string>(
        classifiers['CORRESPONDENCE_CATEGORY_TYPE'],
        correspondence.categoryTypeCode
    ),
    date: correspondence.date,
    documentDate: correspondence.documentDate,
    isOfficial: correspondence.isOfficial,
    officialReference: correspondence.officialReference ?? '',
    employees: sortByField(
        correspondence.employees.map(({ employee }) => employee),
        'name'
    ),
    sendersOrRecipients: correspondence.parties
        .filter(
            (correspondenceParty) =>
                correspondenceParty.roleTypeCode === CorrespondencePartyRoleType.RECEIVER ||
                correspondenceParty.roleTypeCode === CorrespondencePartyRoleType.SENDER
        )
        .map(({ mainParty, extraParty, correspondencePartyAddress }) => ({
            partyPerson: mainParty,
            partyCompany: extraParty,
            correspondencePartyAddress: correspondencePartyAddress
                ? {
                      label: formatAddressSimple(correspondencePartyAddress),
                      value: correspondencePartyAddress,
                  }
                : null,
        })),
    note: correspondence.note ?? '',
    isHandled: correspondence.isHandled,
    statusTypeCode: getClassifierOptionByCode<string, string>(
        classifiers['CORRESPONDENCE_STATUS_TYPE'],
        correspondence.statusTypeCode
    ),
    clients: sortByField(
        correspondence.parties
            .filter((correspondenceParty) => correspondenceParty.roleTypeCode === CorrespondencePartyRoleType.CLIENT)
            .flatMap((correspondenceParty) => [correspondenceParty.mainParty, correspondenceParty.extraParty])
            .filter((party) => party !== null),
        'name'
    ),
    matters: correspondence.matters,
    assignments: correspondence.assignments ?? [],
    dmsLink: correspondence.dmsLink ?? '',
});

/**
 * The create or update request of the correspondence form. The server replaces every collection with the one sent,
 * so each goes in full.
 */
export const correspondenceFormToRequest = (form: CorrespondenceForm): CorrespondenceCreateDto => ({
    name: form.name,
    typeCode: form.typeCode?.value || null,
    directionTypeCode: form.directionTypeCode,
    categoryTypeCode: form.categoryTypeCode?.value || null,
    date: form.date,
    documentDate: form.documentDate,
    isOfficial: form.isOfficial,
    officialReference: form.officialReference || null,
    note: form.note || null,
    statusTypeCode: form.statusTypeCode?.value || null,
    isHandled: form.isHandled,
    dmsLink: form.dmsLink || null,
    employees: form.employees.map(({ id }) => ({ id })),
    matters: form.matters.map(({ id }) => ({ id })),
    assignments: form.assignments.map(({ id }) => ({ id })),
    sendersOrRecipients: form.sendersOrRecipients.map(({ partyCompany, partyPerson, correspondencePartyAddress }) => ({
        partyPerson: partyPerson?.id != null ? { id: partyPerson.id } : null,
        partyCompany: partyCompany?.id != null ? { id: partyCompany.id } : null,
        correspondencePartyAddress: correspondencePartyAddress?.value ?? null,
    })),
    clients: form.clients.map(({ id }) => ({ id })),
});

/**
 * The form fields each section of the correspondence page edits. The number and the direction are fixed once
 * created; the status stays with the type and dates it makes required.
 */
export const CORRESPONDENCE_SECTION_FIELDS = {
    general: ['name', 'typeCode', 'categoryTypeCode', 'date', 'documentDate', 'statusTypeCode', 'isHandled'],
    official: ['isOfficial', 'officialReference'],
    senders: ['employees', 'sendersOrRecipients'],
    note: ['note'],
    connected: ['clients', 'matters', 'assignments'],
    dms: ['dmsLink'],
} satisfies Record<string, (keyof CorrespondenceForm)[]>;

export type CorrespondenceSectionId = keyof typeof CORRESPONDENCE_SECTION_FIELDS;

/**
 * Whether the senders or recipients pass the rules the status and type set on the form's entries: outside Draft each
 * needs a person or a company, and an address unless the correspondence is an email. No entry shows as one empty one.
 */
export const areSendersComplete = ({ statusTypeCode, typeCode, sendersOrRecipients }: CorrespondenceForm): boolean =>
    statusTypeCode?.value === CorrespondenceStatusType.DRAFT ||
    (sendersOrRecipients.length > 0 &&
        sendersOrRecipients.every(
            ({ partyPerson, partyCompany, correspondencePartyAddress }) =>
                (partyPerson || partyCompany) &&
                (typeCode?.value === CorrespondenceType.EMAIL || correspondencePartyAddress)
        ));
